import { TextField, FormControlLabel, Switch } from '@mui/material'
import CustomModal from '../../../components/customModal'
import { format } from 'date-fns'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { FormEvent, useEffect, useState } from 'react'
import { editNote, newNote } from '../../../services/rapports'
import { useRapportsStore } from '../../../store/rapportsStore'
import { useAlertStore } from '../../../store/alertStore'
import { useDoctorStore } from '../../../store/doctor'
import { SPECIAL_SLOT_HEALTH_COVERAGES } from '../../../utils/constants'
import { getMedicalProfile } from '../../../services/doctor'

const NoteModal = ({
  openModal,
  setOpenModal,
  previousNote,
  setPreviousNote,
  rapportId
}: {
  openModal: boolean
  setOpenModal: (open: boolean) => void
  previousNote: { note: string; date: Date | null; id?: number }
  setPreviousNote: (data: { note: string; date: Date | null }) => void
  rapportId: number
}) => {
  const { setNotes } = useRapportsStore()
  const { setAlert } = useAlertStore()
  const [useTemplate, setUseTemplate] = useState(false)
  const { medicalProfile, setMedicalProfile } = useDoctorStore()
  
  const TEMPLATE_TEXT = `INSTRUCCIONES DE ANTICOAGULACIÓN

Fecha: ${format(new Date(), 'dd/MM/yyyy')}

RIN: [COMPLETAR VALOR]
Próximo control: [COMPLETAR FECHA]

DOSIS ACENOCUMAROL (COMPRIMIDOS)

Lunes:     [COMPLETAR DOSIS]
Martes:    [COMPLETAR DOSIS]
Miércoles: [COMPLETAR DOSIS]
Jueves:    [COMPLETAR DOSIS]
Viernes:   [COMPLETAR DOSIS]
Sábado:    [COMPLETAR DOSIS]
Domingo:   [COMPLETAR DOSIS]`
  
  const initialValues = {
    note: ''
  }

  const validationSchema = yup.object({
    note: yup
      .string()
      .max(700, 'El texto es demasiado largo')
      .required('Ingrese una nota')
  })

  const {
    handleSubmit,
    values,
    handleChange,
    handleBlur,
    resetForm,
    errors,
    touched,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      if (previousNote.id) {
        const request = await editNote(rapportId, previousNote.id, values.note, useTemplate)
        const { response } = request

        if (response && response.status === 400) {
          setAlert({
            isOpen: true,
            message: response.data.message,
            severity: 'error'
          })
          handleCloseModal()
        }

        if (request.status === 200) {
          setAlert({
            isOpen: true,
            message: 'Se editó exitosamente la nota',
            severity: 'success'
          })
          setNotes(request.data)
          handleCloseModal()
        }
      } else {
        const response = await newNote(rapportId, values.note, useTemplate)

        if (response.status === 201) {
          setAlert({
            isOpen: true,
            message: 'Se creó exitosamente la nota',
            severity: 'success'
          })
          setNotes(response.data)
          handleCloseModal()
        } else {
          setAlert({
            isOpen: true,
            message: response.data.message,
            severity: 'error'
          })
          handleCloseModal()
        }
      }
    }
  })

  useEffect(() => {
    const apiCall = async () => {
      const data = await getMedicalProfile();
      setMedicalProfile(data);
    };

    apiCall()
  }, [setMedicalProfile]);

  useEffect(() => {
    setFieldValue('note', previousNote?.note)
  }, [previousNote, setFieldValue])

  const handleToggleTemplate = (checked: boolean) => {
    setUseTemplate(checked)
    if (checked) {
      setFieldValue('note', TEMPLATE_TEXT)
    }
  }

  const parseTitle = (): string => {
    if (previousNote.date) {
      const { date } = previousNote
      return `Editar nota - ${format(date, 'dd/MM/yy')}`
    } else {
      return `Nueva nota - ${format(new Date(), 'dd/MM/yy')}`
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    const data = { note: '', date: null }
    setPreviousNote(data)
    resetForm()
    setUseTemplate(false)
  }

  return (
    <CustomModal
      openModal={openModal}
      title={parseTitle()}
      description=""
      submitLabel="Guardar"
      onSubmit={(e: FormEvent<HTMLFormElement> | undefined) => handleSubmit(e)}
      showCancelButton
      handleClose={() => handleCloseModal()}
      imgSrc={null}
      child={
        <div className="w-full flex flex-col justify-center items-left pb-4">
          {medicalProfile.healthCoverages.length > 0 && medicalProfile.healthCoverages.some((h) => SPECIAL_SLOT_HEALTH_COVERAGES.includes(h.tag)) && <FormControlLabel
            control={
              <Switch
                checked={useTemplate}
                onChange={(e) => handleToggleTemplate(e.target.checked)}
                color="primary"
              />
            }
            label="Incluir instrucciones de anticoagulación"
            className="mb-4"
          />}
          <TextField
            variant="outlined"
            multiline
            rows={15}
            id="note"
            className="w-full"
            value={values.note}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.note && touched.note}
            helperText={
              !!errors.note && touched.note !== undefined && errors.note
            }
          />
        </div>
      }
      isSubmitDisable={null}
    />
  )
}

export default NoteModal
